import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const ENG_FREQ = 'ETAOINSHRDLCUMWFGYPBVKJXQZ';

const SAMPLES = [
  {
    label: '🦊 Classic Pangram',
    text: 'WKH TXLFN EURZQ IRA MXPSV RYHU WKH ODCB GRJ',
    answer: 'THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG',
  },
  {
    label: '🧠 Einstein Quote',
    text: 'WZR WKLQJV DUH LQILQLWH: WKH XQLYHUVH DQG KXPDQ VWXSLGLWB',
    answer: 'TWO THINGS ARE INFINITE: THE UNIVERSE AND HUMAN STUPIDITY',
  },
  {
    label: '💪 Motivational',
    text: 'SUDFWLFH PDNHV SHULHFW',
    answer: 'PRACTICE MAKES PERFECT',
  },
  {
    label: '📚 Shakespeare',
    text: 'WR EH RU QRW WR EH WKDW LV WKH TXHVWLRQ',
    answer: 'TO BE OR NOT TO BE THAT IS THE QUESTION',
  },
];

function getFrequency(text) {
  const freq = {};
  for (const ch of text.toUpperCase()) {
    if (/[A-Z]/.test(ch)) freq[ch] = (freq[ch] || 0) + 1;
  }
  return freq;
}

export default function CryptogramSolver() {
  const [cipherText, setCipherText] = useState('');
  const [mapping, setMapping] = useState({});
  const [showAnswer, setShowAnswer] = useState(false);
  const [sampleAnswer, setSampleAnswer] = useState('');
  const [copied, setCopied] = useState(false);

  const freq = useMemo(() => getFrequency(cipherText), [cipherText]);
  const maxFreq = useMemo(() => Math.max(...Object.values(freq), 1), [freq]);

  const cipherLetters = useMemo(() =>
    Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([l]) => l),
    [freq]
  );

  const totalUnique = cipherLetters.length;
  const mappedCount = Object.keys(mapping).filter(k => cipherLetters.includes(k)).length;
  const progress = totalUnique ? Math.round((mappedCount / totalUnique) * 100) : 0;

  function decode(text) {
    return text.toUpperCase().split('').map(ch =>
      /[A-Z]/.test(ch) ? (mapping[ch] || '·') : ch
    ).join('');
  }

  function setMap(cipher, plain) {
    setMapping(prev => {
      const next = { ...prev };
      const upper = plain.toUpperCase();
      if (upper && /^[A-Z]$/.test(upper)) next[cipher] = upper;
      else delete next[cipher];
      return next;
    });
  }

  function loadSample(sample) {
    setCipherText(sample.text);
    setMapping({});
    setShowAnswer(false);
    setSampleAnswer(sample.answer);
  }

  function autoHint() {
    const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
    const newMapping = { ...mapping };
    let hinted = 0;
    for (let i = 0; i < sorted.length && hinted < 3; i++) {
      const [cl] = sorted[i];
      if (!newMapping[cl]) { newMapping[cl] = ENG_FREQ[i]; hinted++; }
    }
    setMapping(newMapping);
  }

  function copyDecoded() {
    const decoded = decode(cipherText).replace(/·/g, '_');
    navigator.clipboard.writeText(decoded).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const decoded = cipherText ? decode(cipherText) : '';

  return (
    <div className="game-page">
      <PageSEO page="cryptogram" />
      <div className="container" style={{ maxWidth: 820 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔐</span>
          <h1>Cryptogram Solver</h1>
          <p>Decode cryptograms, Cryptoquips, and Cryptoquotes by mapping each cipher letter to its real letter. Use frequency analysis to crack the code!</p>
        </div>

        {/* Samples */}
        <div className="solver-card mb-3">
          <div className="fw-bold mb-2">Try a sample cryptogram:</div>
          <div className="d-flex flex-wrap gap-2">
            {SAMPLES.map(s => (
              <button key={s.label}
                className="btn btn-sm fw-bold"
                style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito, sans-serif' }}
                onClick={() => loadSample(s)}>{s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cipher input */}
        <div className="solver-card">
          <label className="fw-bold mb-2 d-block">Enter your cryptogram:</label>
          <textarea
            className="form-control mb-3"
            rows={3}
            value={cipherText}
            onChange={e => { setCipherText(e.target.value); setMapping({}); setShowAnswer(false); setSampleAnswer(''); }}
            placeholder="Paste or type your encoded message here… (letters only, uppercase or lowercase)"
            style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2, borderRadius: 12 }}
          />
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: 'var(--primary)', color: '#fff', border: 'none' }}
              onClick={autoHint} disabled={!cipherText}>💡 Frequency Hint (3 letters)</button>
            <button className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none' }}
              onClick={() => setMapping({})} disabled={!Object.keys(mapping).length}>🗑 Clear Mapping</button>
            <button className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: '#fee2e2', color: '#991b1b', border: 'none' }}
              onClick={() => { setCipherText(''); setMapping({}); setSampleAnswer(''); setShowAnswer(false); }}>✕ Clear All</button>
          </div>
        </div>

        {cipherText.trim() && (
          <>
            {/* Progress bar */}
            <div className="solver-card">
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                <span className="fw-bold">Decoding Progress</span>
                <span style={{ color: '#6b7280' }}>{mappedCount} / {totalUnique} letters mapped</span>
              </div>
              <div style={{ background: '#f1f5f9', borderRadius: 99, height: 10, overflow: 'hidden' }}>
                <div style={{
                  width: `${progress}%`, height: '100%', borderRadius: 99, transition: 'width 0.3s ease',
                  background: progress === 100 ? '#10b981' : 'linear-gradient(90deg,#4f46e5,#7c3aed)'
                }} />
              </div>
              {progress === 100 && (
                <p className="text-center mt-2 mb-0 fw-bold" style={{ color: '#059669' }}>
                  🎉 All letters mapped! Check your decoded message below.
                </p>
              )}
            </div>

            {/* Frequency analysis */}
            <div className="solver-card">
              <h5 className="fw-bold mb-1">📊 Letter Frequency Analysis</h5>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.8rem' }}>
                In English: <strong>E, T, A, O, I, N</strong> are most common. Match your highest-frequency cipher letters to these.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-flex-end">
                {Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([letter, count]) => (
                  <div key={letter} className="text-center" style={{ minWidth: 44 }}>
                    <div style={{ height: 56, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: 3 }}>
                      <div style={{
                        width: 26, borderRadius: '4px 4px 0 0', transition: 'background 0.2s',
                        background: mapping[letter] ? '#10b981' : '#4f46e5',
                        height: `${Math.round((count / maxFreq) * 50) + 6}px`,
                      }} />
                    </div>
                    <div style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: '0.9rem', color: '#1e1b4b' }}>{letter}</div>
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{count}×</div>
                    {mapping[letter] && (
                      <div style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 700 }}>→{mapping[letter]}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Letter mapping */}
            <div className="solver-card">
              <h5 className="fw-bold mb-1">🔑 Letter Mapping</h5>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.8rem' }}>
                For each cipher letter, type the real letter you think it represents.
              </p>
              <div className="d-flex flex-wrap gap-2">
                {cipherLetters.map(cl => (
                  <div key={cl} className="text-center" style={{
                    padding: '0.45rem 0.5rem', minWidth: 60, borderRadius: 10,
                    border: `2px solid ${mapping[cl] ? '#10b981' : '#e2e8f0'}`,
                    background: mapping[cl] ? '#ecfdf5' : '#f8fafc',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', fontFamily: 'monospace', color: '#4f46e5' }}>{cl}</div>
                    <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginBottom: 3 }}>{freq[cl]}×</div>
                    <input
                      type="text"
                      maxLength={1}
                      value={mapping[cl] || ''}
                      onChange={e => setMap(cl, e.target.value)}
                      style={{
                        width: 34, textAlign: 'center', fontWeight: 800, fontSize: '1rem',
                        fontFamily: 'monospace', border: '1px solid #d1d5db', borderRadius: 6,
                        background: 'white', padding: '2px 0', color: '#059669', textTransform: 'uppercase'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Decoded output */}
            <div className="solver-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-bold mb-0">🔓 Decoded Message</h5>
                <button className="btn btn-sm fw-bold"
                  style={{ borderRadius: 50, background: copied ? '#d1fae5' : 'var(--border)', border: 'none' }}
                  onClick={copyDecoded}>{copied ? '✓ Copied!' : '📋 Copy'}</button>
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2,
                lineHeight: 2.2, padding: '1rem', background: '#f8fafc',
                borderRadius: 12, minHeight: 64, wordBreak: 'break-word'
              }}>
                {decoded.split('').map((ch, i) => (
                  <span key={i} style={{ color: ch === '·' ? '#d1d5db' : '#059669', fontWeight: ch !== '·' ? 700 : 400 }}>{ch}</span>
                ))}
              </div>
              {sampleAnswer && (
                <div className="mt-3">
                  <button className="btn btn-sm fw-bold"
                    style={{ borderRadius: 50, background: '#fef3c7', border: 'none', color: '#92400e' }}
                    onClick={() => setShowAnswer(v => !v)}>
                    {showAnswer ? '🙈 Hide Answer' : '👁 Reveal Answer'}
                  </button>
                  {showAnswer && (
                    <div className="mt-2 p-3 rounded-3 fw-bold"
                      style={{ background: '#ecfdf5', fontFamily: 'monospace', letterSpacing: 1, color: '#065f46', fontSize: '1rem' }}>
                      {sampleAnswer}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* How it works tip */}
            <div className="solver-card" style={{ background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', border: 'none' }}>
              <h6 className="fw-bold mb-2">💡 Solving Tips</h6>
              <ul className="mb-0" style={{ fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>Single-letter words must be <strong>A</strong> or <strong>I</strong></li>
                <li>Two-letter words are often: <strong>OF, TO, IN, IS, IT, BE, AS, AT, SO, WE, HE, BY</strong></li>
                <li>The most frequent letter in English is <strong>E</strong> — try mapping your most common cipher letter to E</li>
                <li>Look for double letters — common doubles: <strong>LL, SS, EE, TT, FF, OO, PP, RR</strong></li>
                <li>Apostrophe words: _'T = IT/AT, _'S = IS, _'RE = ARE/WE'RE</li>
              </ul>
            </div>
          </>
        )}

        <SeoContent game="cryptogram" />
      </div>
    </div>
  );
}
